/*
 * Filename: template-config-controllers.ts
 * Description: template config controller.
 */

import { CQAPIService } from '../../services/http-api.service';
import { TemplateSFObjectStore } from '../../store/models/template-object.model';
import { LSTemplatesStore } from '../../store/ls-template-store';
import { CQAppStore } from '../../store/cq-app-store';
import FileSaver from 'file-saver';
import { CQAppConstant } from '../../app-constants';
import { CQMasterDataStore } from '../../store/cq-master-data.store';
import { BaseClassDataStore } from '../../store/base-class-store';
import { UserDetailsStore, IUserDetailsStore } from '../../store/user-details.store';
import Cookies from 'universal-cookie';
import { Utility } from '../../common/utility';
import { CQStoreManager } from './../../store/store-manager';


interface FieldObject {
    [key: string]: any
}

export default class TemplateConfigurationController {
    private readonly cqAPIService: CQAPIService;
    private readonly storeManagerCtrl: CQStoreManager;
    public userDetailsStore: IUserDetailsStore;
    private readonly cookie: Cookies;
    private cqUtilityClass: Utility;
    constructor() {
        this.storeManagerCtrl = new CQStoreManager();
        this.cqAPIService = new CQAPIService();
        this.cqUtilityClass = new Utility();
        //this.templateSFObjectStore = TemplateSFObjectStore.create({ sfObjects: [], templateLayouts: [] });
        this.userDetailsStore = UserDetailsStore;
        this.cookie = new Cookies();
    }

    public getCookieValue = (key: string) => {
        return this.cookie.get(key);
    }

    /**
     * sets the prefix-name to local storage or global store conditionally depending upon set store
     * @param {string} templateName stores the name of the selected template
     * @param {boolean} setStore if true then updates the store value of prefix
     * @param {string} prefixValue stores the value of prefix-name of selected template
     * @memberof TemplateConfigurationController
     */
    public setPrefixName = (templateName: string, setStore: boolean = true, prefixValue?: string) => {
        let orgId = this.getCookieValue(CQAppConstant.LOGGED_IN_ORG_ID);
        let stringPrefixNames: any = localStorage.getItem('prefixName');
        if (!stringPrefixNames) {
            stringPrefixNames = JSON.stringify({});
        }
        let prefixNames = JSON.parse(stringPrefixNames);
        if (prefixValue) {
            prefixNames[orgId] = prefixValue;
            localStorage.setItem('prefixName', JSON.stringify(prefixNames));
        }
        if (setStore === true) {
            this.updateInputField(templateName, 'prefixName', prefixNames[orgId]);
        }
    }

    /**
     * @memberof TemplateConfigurationController
     */
    public async getObjects() {
        try {
            CQAppStore.loader.setVisibility(true);
            await this.cqAPIService.getObjects().then((objects) => {
                TemplateSFObjectStore.loadSFObjects(this.filterObjects(objects.sobjects));
                CQAppStore.loader.setVisibility(false);
            });
        } catch (error : any) {

            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    }

    /**
     * @param {string} objectName
     * @memberof TemplateConfigurationController
     */
    public getObjectFields(templateName: string, key: string, objectName: string, hasParent: boolean) {
        this.cqAPIService.getApiObjectFields(objectName, hasParent)
        .then((objectFields) => {
            if (objectFields) {
                TemplateSFObjectStore.loadSFObjectFields(objectFields);
                CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(templateName, key, objectFields);
            }
        })
        .catch((error) => {
            CQMasterDataStore.OutputConfig.resetFromKey(templateName, [key]);
        });
    }

    public getFilterFields = (fields: any[]) => {
        return fields.map((field: any) => {
            return {
                fieldFullName: field.name,
                fieldLabel: field.label,
                fieldType: field.type,
                referenceTo: field.referenceTo,
                relationshipName: field.relationshipName,
                length: field.length
            }
        }).sort((a: any, b: any) => { return a.fieldLabel > b.fieldLabel ? 1 : (a.fieldLabel === b.fieldLabel ? 0 : -1); });
    }

    /**
    * @param {string} objectName
    * @memberof TemplateConfigurationController
    */
    public async getBaseClassNames(objectName: string, orgNameSpace: string) {
        try {
            CQAppStore.loader.setVisibility(true);
            const baseClassesInStore = BaseClassDataStore.BaseClassObject;
            if(baseClassesInStore.length>0 && baseClassesInStore[0].objects.hasOwnProperty(objectName)){
                CQAppStore.loader.setVisibility(false);
            }else{
                let baseClassNames = await this.cqAPIService.getBaseClassNames(objectName, orgNameSpace)
                if(baseClassNames.length>0 && baseClassNames[0].objects.hasOwnProperty(objectName)){
                    const newObjectProperty = baseClassNames[0].objects[objectName] ;
                    baseClassesInStore[0].objects[objectName] = newObjectProperty;
                    BaseClassDataStore.setBaseClassObject(baseClassesInStore);
                }
                CQAppStore.loader.setVisibility(false);
            }
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    }

    /**
     * @description queries quick-actions according to object-name and updates store
     * @param {string} objectName
     * @memberof TemplateConfigurationController
     * @returns Promise
     */
     public async getQuickActions(objectName: string) {
        const quickActions = TemplateSFObjectStore.quickActions;
        if(quickActions.length === 0 ){
            try {
                CQAppStore.loader.setVisibility(true);
                await this.cqAPIService.getQuickActions(objectName).then((quickActions) => {
                    quickActions.forEach((action: any) => {
                        if (action.NamespacePrefix === null) {
                            action.NamespacePrefix = ''
                        }
                    }
                    )
                    if (quickActions) {
                        quickActions = quickActions.sort((a: any, b: any) => { return a.Name > b.Name ? 1 : (a.Name === b.Name ? 0 : -1); });
                        TemplateSFObjectStore.loadQuickActions(quickActions);
                    }
                    CQAppStore.loader.setVisibility(false);
                });
            } catch (error : any) {
                CQAppStore.loader.setVisibility(false);
                const errorObj = {
                    errorType: CQAppConstant.ERROR_CUSTOM,
                    errorMessage: error.error ? error.error : error.message,
                    errorStatus: error.status,
                    componentName: ''
                };
                CQAppStore.error.dispatchError(errorObj);
            }
        }
    }

    /**
     * @param {string} objectName
     * @memberof TemplateConfigurationController
     */
    public async getLayouts(objectName: string) {
        try {
            CQAppStore.loader.setVisibility(true);
            await this.cqAPIService.getLayouts(objectName).then((layouts) => {
                if (layouts) {
                    layouts = layouts.sort((a: any, b: any) => { return a.Name > b.Name ? 1 : (a.Name === b.Name ? 0 : -1); });
                    TemplateSFObjectStore.loadLayouts(layouts);
                }
                CQAppStore.loader.setVisibility(false);
            });
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    }

    /**
     * @param {string} templateName
     * @param {string} objectName
     * @memberof TemplateConfigurationController
     */
    public setObject(templateName: string, objectName: string) {
        CQMasterDataStore.OutputConfig.setSelectedObject(templateName, objectName);
    }

    /**
     * @memberof TemplateConfigurationController
     */
    public resetLayoutList() {
        TemplateSFObjectStore.resetLayouts();
    }

    /**
     * @param {string} templateName
     * @param {string} layoutId
     * @memberof TemplateConfigurationController
     */
    public setLayout(templateName: string, layoutId: string) {
        CQMasterDataStore.OutputConfig.setSelectedLayout(templateName, layoutId);
    }

    /**
     * @param {string} selectedLayoutName
     * @param {string} objectName
     * @param {string} TableEnumOrId
     * @memberof TemplateConfigurationController
     */
    public async getLayout(templateName: string, selectedLayoutName: string, objectName: string, TableEnumOrId: string) {
        try {
            CQAppStore.loader.setVisibility(true);
            await this.cqAPIService.getLayout(selectedLayoutName, objectName, TableEnumOrId).then((layoutMetaData: any) => {
                const { fields } = layoutMetaData;
                layoutMetaData.metadata.fields = fields;
                if(Object.keys(layoutMetaData.layout).length>0){
                    const { layoutSections, layoutFields } = this.getUpdateLayoutData(layoutMetaData);
                    layoutMetaData.layout.layoutSections = layoutSections;
                    layoutMetaData.fields = layoutFields;
                }
                CQMasterDataStore.OutputConfig.setLayoutMetadata(templateName, 'layout', layoutMetaData);
                CQAppStore.loader.setVisibility(false);
            });
        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    }

    /**
     * @param {*} layoutMetaData
     * @returns
     * @memberof TemplateConfigurationController
     */
    public getUpdateLayoutData(layoutMetaData: any) {
        const layoutSections: any[] = [];
        const layoutFields: any[] = [];
        layoutMetaData.layout.layoutSections.forEach((layoutSection: any) => {
            if (layoutSection.detailHeading === true || layoutSection.editHeading === true) {
                layoutSections.push(layoutSection);
                layoutSection.columnSize = layoutSection.layoutColumns.length;
                let maxColumnSize = 0;
                // find the column with maximum number of fields/form items, so that we can iterate and create a single list from multi columns.
                layoutSection.layoutColumns.forEach(function (e: any) {
                    if (e.layoutItems && e.layoutItems.length > maxColumnSize) {
                        maxColumnSize = e.layoutItems.length;
                    }
                });
                const { updatedLayoutFields, layoutItems } = this.getUpdatedLayoutFields(maxColumnSize, layoutSection);
                layoutFields.push(...updatedLayoutFields);
                layoutSection.layoutColumns = layoutItems;
            }
        });
        return { layoutSections, layoutFields };
    }

    public getUpdatedLayoutFields(maxColumnSize: number, layoutSection: any) {
        let index = 0, columnIndex = 0;
        const updatedLayoutFields: any[] = [];
        const layoutItems: any = [];
        for (; index < maxColumnSize; index++) {
            for (columnIndex = 0; columnIndex < layoutSection.columnSize; columnIndex++) {
                const sectionItem = layoutSection.layoutColumns[columnIndex].layoutItems[index] || { emptySpace: true };
                layoutItems.push(sectionItem);
                if (sectionItem.field) {
                    updatedLayoutFields.push(sectionItem.field);
                }
            }
        }
        return { updatedLayoutFields, layoutItems };
    }

    public filterObjects(sobjects: any) {
        if (sobjects && sobjects.length !== 0) {
            sobjects = sobjects.sort((a: any, b: any) => { return a.label > b.label ? 1 : (a.label === b.label ? 0 : -1); });
            return sobjects.filter((sObject: any) => {
                return ((sObject.createable && sObject.custom )|| CQAppConstant.FIELD_API_NAMES.indexOf(sObject.name)>-1);
            }).map((filteredSObject: any) => {
                filteredSObject.label = `${filteredSObject.label} ( ${filteredSObject.name} )`;
                return filteredSObject;
            });
        }
        return [];
    }

    public getSelectedTemplate() {
        let template: any = LSTemplatesStore.selectedTemplate;
        if (template.hasOwnProperty('selectedTemplate')) {
            template = template.selectedTemplate;
        }
        return template;
    }

    public isLayoutFieldAvailable() {
        let template: any = LSTemplatesStore.selectedTemplate;
        if (template.hasOwnProperty('selectedTemplate')) {
            template = template.selectedTemplate;
        }
        if (template.inputParams.layout) {
            return true;
        }
        return false;
    }

    public getTemplateWiseFormData(templateName: string) {
        return CQMasterDataStore.OutputConfig.getStoreData(templateName);
    }

    /**
     * @param {string} templateName
     * @memberof TemplateConfigurationController
     */
    public async downloadLWC(templateName: string) {
        const outputConfigData = this.getTemplateWiseFormData(templateName);
        try {
            if (templateName.includes('cqGridGenerator')) {
                const ifNotValidJSON = outputConfigData.headerActions.some((action: any) => {
                    return (!action.hasOwnProperty('componentName') && !action.componentName);
                });
                if (ifNotValidJSON) {
                    alert('Please select Component Name for all the actions');
                } else {
                    const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                    if (outputConfigData) {
                        const metadata = JSON.parse(JSON.stringify(outputConfigData));
                        if (templateName.includes('cqGridGenerator')) {
                            metadata.additionalFields = this.getAddtionalFields(templateName);
                            metadata.parentFields = [...CQAppConstant.DEFAULT_PARENT_FIELDS,...this.getParentFields(templateName)];
                            metadata.parentFields  = Array.from(new Set(metadata.parentFields));
                        }
                        else {
                            if (metadata.hasOwnProperty('additionalFields')) {
                                metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata, templateName)));
                            }
                        }
                        // End Delete standard fields
                        CQAppStore.loader.setVisibility(true);
                        const selectedTemplateName:any = LSTemplatesStore.selectedTemplate;
                        templateName = selectedTemplateName.name?
                            selectedTemplateName.name:selectedTemplateName.selectedTemplate.name;
                        await this.cqAPIService.downloadLWC(versionNumber, templateName, metadata).then((res) => {
                            if (res) {
                                CQAppStore.loader.setVisibility(false);
                                return res.blob();
                            }
                        }).then((templateZipBlob) => {
                            if (templateZipBlob) {
                                FileSaver.saveAs(templateZipBlob, CQAppConstant.DOWNLOADABLE_ZIP_FILE_NAME);
                            }
                        });
                    }
                }
            } else {
                const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                if (outputConfigData) {
                    const metadata = JSON.parse(JSON.stringify(outputConfigData));
                    if (templateName.includes('cqGridGenerator')) {
                        metadata.additionalFields = this.getAddtionalFields(templateName);
                    }
                    else {
                        if (metadata.hasOwnProperty('additionalFields')) {
                            metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata, templateName)));
                        }
                    }
                    // End Delete standard fields
                    CQAppStore.loader.setVisibility(true);
                    const selectedTemplateName:any = LSTemplatesStore.selectedTemplate;
                    templateName = selectedTemplateName.name?
                        selectedTemplateName.name:selectedTemplateName.selectedTemplate.name;
                    await this.cqAPIService.downloadLWC(versionNumber, templateName, metadata).then((res) => {
                        if (res) {
                            CQAppStore.loader.setVisibility(false);
                            return res.blob();
                        }
                    }).then((templateZipBlob) => {
                        if (templateZipBlob) {
                            FileSaver.saveAs(templateZipBlob, CQAppConstant.DOWNLOADABLE_ZIP_FILE_NAME);
                        }
                    });
                }
            }
        }
        catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }

    }

    /**
     * @param {string} templateName
     * @param {string} inputKey
     * @param {*} value
     * @memberof TemplateConfigurationController
     */
    public updateInputField(templateName: string, inputKey: string, value: any) {
        CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(templateName, inputKey, value);
    }

    /**
     *
     * @param { string } templateName
     * @memberof TemplateConfigurationController
     */
    public generateComponentName(templateName: string): string{
        let componentName: string = '';
        const outputConfigData = CQMasterDataStore.OutputConfig.getStoreData(templateName);

        if(templateName.includes('cqForm')){
            const { prefixName, suffixName, selectedObject } = outputConfigData;
            componentName = 'cqui' + prefixName + this.cqUtilityClass.camelize(selectedObject) + suffixName;
        }

        if(templateName.includes('cqEsig')){
            const { esigAction, objectApiName, prefixName  } = outputConfigData;
            componentName = 'cqui_'+ (prefixName? prefixName + '_' : "") + this.cqUtilityClass.camelize(objectApiName)  + '_' + esigAction;
        }

        if(templateName.includes('cqEsigAction')){
            const { taskType, objectApiName, prefixName } = outputConfigData;
            componentName = 'cqui_' + (prefixName ? prefixName + '_' : '') + this.cqUtilityClass.camelize(objectApiName)  + '_' + taskType;
        }

        if(templateName.includes('cqGridGenerator')){
            const { prefixName, objectApiName, suffixName } = outputConfigData;
            componentName = 'cqui' + prefixName + this.cqUtilityClass.camelize(objectApiName) + suffixName + 'Grid';
        }

        if(templateName.includes('cqNBA')){
            const { prefixName, suffixName, selectedObject } = outputConfigData;
            componentName = 'cqui' + prefixName + this.cqUtilityClass.camelize(selectedObject)+ 'Nba' + suffixName;
        }

        return componentName;
    }

    /**
     * @description fetches the layouts from the org and updates the store
     * @param {string} templateName
     * @memberof TemplateConfigurationController
     */
    public refreshLayout = async (templateName:string) => {
        const layoutId = CQMasterDataStore.OutputConfig.getSelectedLayout(templateName);
        const layoutObj: any = TemplateSFObjectStore.templateLayouts.filter((templateLayout: any) => {
            return templateLayout.Id === layoutId;
        });
        const selectedObject = CQMasterDataStore.OutputConfig.getSelectedObject(templateName);
        await this.getLayout(templateName, layoutObj[0].Name, selectedObject, layoutObj[0].TableEnumOrId);

        this.clearFieldRulesConfigurations(templateName);
        const outputConfig = this.getTemplateWiseFormData(templateName);
        outputConfig.template = templateName;
        this.storeManagerCtrl.storeDeployedTemplate(outputConfig);
    }

    private sameSelectedBaseClassAndComponentNamePopUp(componentName: string, className: string){
        if(componentName === className){
            const errorObj = {
                errorType: CQAppConstant.SAME_BASE_CLASS_ERROR,
                errorMessage: CQAppConstant.SAME_BASE_CLASS_ERROR,
                errorMessages: [CQAppConstant.ERROR_MESSAGES.SAME_BASE_CLASS_ERROR_MESSAGE],
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);

            return true;
        }

        return false;
    }

    private sameBaseClassAndComponentNamePopUp(templateName: string, componentName: string, baseClassArray: string[]){
        let shouldContinue = true;
        for(const baseClass of baseClassArray!){
            if(componentName === baseClass){
                shouldContinue = false;
                break;
            }
        }

        if(!shouldContinue){
            const errorObj = {
                errorType: CQAppConstant.BASE_CLASS_ALREADY_EXIST_WARNING,
                errorMessage: CQAppConstant.ERROR_MESSAGES.BASE_CLASS_ALREADY_EXIST_WARNING_MESSAGE,
                errorMessages: [],
                componentName: templateName
            };
            CQAppStore.error.dispatchError(errorObj);

            return true;
        }

        return false;
    }

    private componentNameAlreadyExistPopUp(componentName: string, templateName: string){
        const promise = new Promise(async (resolve: any, reject: any) => {
            CQAppStore.loader.setVisibility(true, '', true);
            try{
                const queriedComponent = await this.cqAPIService.fetchDeployedTemplates(componentName);
                if(queriedComponent.length > 0){
                    CQAppStore.loader.setVisibility(false);
                    const errorObj = {
                        errorType: CQAppConstant.COMPONENT_NAME_ALREADY_EXIST_WARNING,
                        errorMessage: CQAppConstant.ERROR_MESSAGES.COMPONENT_NAME_ALREADY_EXIST_WARNING_MESSAGE,
                        errorMessages: [],
                        componentName: templateName
                    };
                    CQAppStore.error.dispatchError(errorObj);
                    resolve(true);
                }else{
                    resolve(false);
                }
            }catch(error : any){
                CQAppStore.loader.setVisibility(false);
                const errorObj = {
                    errorStatus : error.status,
                    errorType: CQAppConstant.ERROR_CUSTOM,
                    errorMessage: error.error ? error.error : error.message ,
                    componentName: ''
                };
                CQAppStore.error.dispatchError(errorObj);
                resolve(true);
            }
        });

        return promise;
    }

    /**
     * @param {string} templateName
     * @memberof TemplateConfigurationController
     */
    public async publishLWC(templateName: string, submitForcefully: boolean, baseClassArray: string[] = []) {
        try {
            if (templateName.includes('cqGridGenerator')) {
                const outputConfigData = this.getTemplateWiseFormData(templateName);
                const ifNotValidJSON = outputConfigData.headerActions.some((action: any) => {
                    return (!action.hasOwnProperty('componentName') && !action.componentName);
                });
                if (ifNotValidJSON) {
                    alert('Please select Component Name for all the actions');
                } else {
                    const outputConfigData = this.getTemplateWiseFormData(templateName);
                    const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                    if (outputConfigData) {
                        const metadata = JSON.parse(JSON.stringify(outputConfigData));
                        if (templateName.includes('cqGridGenerator')) {
                            metadata.additionalFields = this.getAddtionalFields(templateName);
                            metadata.parentFields = [...CQAppConstant.DEFAULT_PARENT_FIELDS,...this.getParentFields(templateName)];
                            metadata.parentFields  = Array.from(new Set(metadata.parentFields));
                        }
                        else {
                            if (metadata.hasOwnProperty('additionalFields')) {
                                metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata, templateName)));
                            }
                        }

                        //regarding publish popup for component
                        if(submitForcefully === false){
                            const componentName = this.generateComponentName(templateName);

                            //check if componentName is present in org
                            const showComponentNameAlreadyExistPopUp = await this.componentNameAlreadyExistPopUp(componentName, templateName);
                            if(showComponentNameAlreadyExistPopUp){
                                return;
                            }
                        }

                        // End Delete standard fields
                        CQAppStore.loader.setVisibility(true, '', true);
                        const selectedTemplateName:any = LSTemplatesStore.selectedTemplate;
                        templateName = selectedTemplateName.name?
                            selectedTemplateName.name:selectedTemplateName.selectedTemplate.name;
                        await this.cqAPIService.publishLWC(versionNumber, templateName, metadata).then((res: any) => {
                            CQAppStore.loader.setVisibility(false);
                            if (res.success === false || res.status === 'Failed') {
                                let failureReasons = [];
                                if (res.details.componentFailures && Array.isArray(res.details.componentFailures)) {
                                    failureReasons = res.details.componentFailures.map((failure: any) => failure.problem);
                                } else {
                                    failureReasons.push(res.details.componentFailures.problem);
                                }
                                const errorObj = {
                                    errorType: CQAppConstant.DEPLOY_FAILED,
                                    errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                                    errorMessages: failureReasons,
                                    componentName: ''
                                };
                                CQAppStore.error.dispatchError(errorObj);
                            } else {
                                const successObj = {
                                    successMessage: 'Package is deployed successfully',
                                    componentName: '',
                                };
                                CQAppStore.success.dispatchError(successObj);
                            }
                            return res;
                        }).catch((error) => {

                            CQAppStore.loader.setVisibility(false);
                            const errorObj = {
                                errorType: CQAppConstant.UNKNOWN_PUBLISH_ERROR,
                                errorMessage: CQAppConstant.ERROR_MESSAGES.CORS_ERROR_MESSAGE,
                                errorStatus: error.status,
                                componentName: ''
                            };
                            CQAppStore.error.dispatchError(errorObj);
                        });
                    }
                }
            } else {
                const outputConfigData = this.getTemplateWiseFormData(templateName);
                const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                if (outputConfigData) {
                    const metadata = JSON.parse(JSON.stringify(outputConfigData));
                    if (templateName.includes('cqGridGenerator')) {
                        metadata.additionalFields = this.getAddtionalFields(templateName);
                    }
                    else {
                        if (metadata.hasOwnProperty('additionalFields')) {
                            metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata, templateName)));
                        }
                    }

                    //regarding publish popup for component
                    if(submitForcefully === false){
                        const { className } = metadata;
                        const componentName = this.generateComponentName(templateName);

                        //check if componentName and selected base class is same
                        const showSameSelectedBaseClassAndComponentNamePopUp = this.sameSelectedBaseClassAndComponentNamePopUp(componentName, className);
                        if(showSameSelectedBaseClassAndComponentNamePopUp){
                            return;
                        }

                        //check if componentName is present in base class array
                        const showSameBaseClassAndComponentNamePopUp = this.sameBaseClassAndComponentNamePopUp(templateName, componentName, baseClassArray);
                        if(showSameBaseClassAndComponentNamePopUp){
                            return;
                        }

                        //check if componentName is present in org
                        const showComponentNameAlreadyExistPopUp = await this.componentNameAlreadyExistPopUp(componentName, templateName);
                        if(showComponentNameAlreadyExistPopUp){
                            return;
                        }
                    }
                    const selectedTemplateName:any = LSTemplatesStore.selectedTemplate;
                    templateName = selectedTemplateName.name?
                        selectedTemplateName.name:selectedTemplateName.selectedTemplate.name;
                    CQAppStore.loader.setVisibility(true, '', true);
                    await this.cqAPIService.publishLWC(versionNumber, templateName, metadata).then((res: any) => {
                        CQAppStore.loader.setVisibility(false);
                        if (res.success === false || res.status === 'Failed') {
                            let failureReasons = [];
                            if (res.details.componentFailures && Array.isArray(res.details.componentFailures)) {
                                failureReasons = res.details.componentFailures.map((failure: any) => failure.problem);
                            } else {
                                failureReasons.push(res.details.componentFailures.problem);
                            }
                            const errorObj = {
                                errorType: CQAppConstant.DEPLOY_FAILED,
                                errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                                errorMessages: failureReasons,
                                componentName: ''
                            };
                            CQAppStore.error.dispatchError(errorObj);
                        } else {
                            const successObj = {
                                successMessage: 'Package is deployed successfully',
                                componentName: '',
                            };
                            CQAppStore.success.dispatchError(successObj);
                        }
                        return res;
                    }).catch((error) => {

                        CQAppStore.loader.setVisibility(false);
                        const errorObj = {
                            errorType: CQAppConstant.UNKNOWN_PUBLISH_ERROR,
                            errorMessage: CQAppConstant.ERROR_MESSAGES.CORS_ERROR_MESSAGE,
                            errorStatus: error.status,
                            componentName: ''
                        };
                        CQAppStore.error.dispatchError(errorObj);
                    });
                }
            }

        } catch (error : any) {

            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }

    }

    public getAddtionalFields = (selectedTemplateName: string) => {
        const outputConfig = CQMasterDataStore.OutputConfig;
        const ruleElements: any = outputConfig.getStoreKeyData(selectedTemplateName, 'ruleElements');

        // const ruleElements: any = CQMasterDataStore.RuleElements.FieldRuleElement;
        let additionalFields: any[] = [];
        if (ruleElements.hasOwnProperty('gridObject')) {
            const gridObject: any = ruleElements.gridObject;
            if (gridObject.gridComponentConfigView && gridObject.gridComponentConfigView.fieldsToDisplay.length !== 0) {
                const fieldsArray: any[] = [];
                gridObject.gridComponentConfigView.fieldsToDisplay.forEach((fields: any, index: any) => {
                    if(typeof fields === 'object' && fields.hasOwnProperty('groupName')){
                        fields.fields.forEach((field: any) => {
                            fieldsArray.push(field['fieldInfo']);
                        })
                    }else{
                        fieldsArray.push(fields);
                    }
                });

                if (gridObject.hasOwnProperty('rowActions')) {
                    const rowActions = gridObject.rowActions;
                    Object.keys(rowActions).forEach((rowActionKey: any) => {
                        const rowAction = rowActions[rowActionKey];
                        const tempAdditionalFields = this.getAddtionalFieldsForGridBasedOnCriteria(rowAction, fieldsArray);
                        additionalFields = [...additionalFields, ...tempAdditionalFields];
                    });
                }

                if (gridObject.gridComponentConfigView.hasOwnProperty('fieldsVisibilityRules')) {
                    const compoundFieldRule = gridObject.gridComponentConfigView['fieldsVisibilityRules'];
                    Object.keys (compoundFieldRule).forEach((fieldRule: any) => {
                        const fields = compoundFieldRule[fieldRule]['fields'];
                        fields.forEach((fieldRule: any) => {
                            if(fieldRule){
                                const tempAdditionalFields = this.getAddtionalFieldsForGridBasedOnCriteria(fieldRule, fieldsArray);
                                additionalFields = [...additionalFields, ...tempAdditionalFields];
                            }
                        })
                    });
                }
            }
            additionalFields = Array.from(new Set(additionalFields));
        }
        return additionalFields;
    }

    /**
     * Get the additional field based on a fieldsArray and field present in rule
     * @param fieldRule
     * @param fieldsArray fields that are added in the grid column
     */
    private getAddtionalFieldsForGridBasedOnCriteria = (fieldRule: any, fieldsArray: any[]) => {
        const additionalFields: string[] = [];
        if (fieldRule.hasOwnProperty('addedCriterias')) {
            fieldRule.addedCriterias.forEach((criteria: any) => {
                if (criteria.fieldType === 'Child') {
                    const isRecordFieldExist = this.checkRecordFieldInFieldToDisplay(fieldsArray, criteria.field);
                    if (!isRecordFieldExist) {
                        additionalFields.push(criteria.field);
                    }
                }
                if (criteria.isDynamic && criteria.compareTo && criteria.compareFieldType === 'Child') {
                    const isRecordFieldExist = this.checkRecordFieldInFieldToDisplay(fieldsArray, criteria.compareTo);
                    if (!isRecordFieldExist) {
                        additionalFields.push(criteria.compareTo);
                    }
                }
            });
        }

        return additionalFields;
    }

    private getParentFields = (selectedTemplateName: string) => {
        const outputConfig = CQMasterDataStore.OutputConfig;
        const ruleElements: any =  outputConfig.getStoreKeyData(selectedTemplateName, 'ruleElements');
        let parentFields: string[] = [];
        if (ruleElements.hasOwnProperty('gridObject')) {
            const gridObject: any = ruleElements.gridObject;
            if (gridObject.gridComponentConfigView) {
                if (gridObject.hasOwnProperty('rowActions')) {
                    const rowActions = gridObject.rowActions;
                    parentFields = [...parentFields, ...this.checkParentFieldsRowActionRules(rowActions)];
                }
                if (gridObject.hasOwnProperty('headerActions')) {
                    const headerActions = gridObject.headerActions;
                    parentFields = [...parentFields, ...this.checkParentFieldsHeaderActionRules(headerActions)];
                }
            }
            parentFields = Array.from(new Set(parentFields));
        }
        return parentFields;
    }

    private checkParentFieldsHeaderActionRules = (headerActions: any) => {
        const parentFields: string[] = [];
        Object.keys(headerActions).forEach((headerActionKey: any) => {
            const headerAction = headerActions[headerActionKey];
            if (headerAction.hasOwnProperty('addedCriterias')) {
                headerAction.addedCriterias.forEach((criteria: any) => {
                    if (criteria.field) {
                        parentFields.push(criteria.field);
                    }
                    if (criteria.isDynamic && criteria.compareTo) {
                        parentFields.push(criteria.compareTo);
                    }
                });
            }
        });
        return parentFields;
    }

    private checkParentFieldsRowActionRules = (rowActions: any) => {
        const parentFields: string[] = [];
        Object.keys(rowActions).forEach((rowActionKey: any) => {
            const rowAction = rowActions[rowActionKey];
            if (rowAction.hasOwnProperty('addedCriterias')) {
                rowAction.addedCriterias.forEach((criteria: any) => {
                    if (criteria.fieldType === 'Parent') {
                        parentFields.push(criteria.field);
                    }
                    if (criteria.isDynamic && criteria.compareTo && criteria.compareFieldType === 'Parent') {
                        parentFields.push(criteria.compareTo);
                    }
                });
            }
        });
        return parentFields;
    }

    private checkRecordFieldInFieldToDisplay(fieldsToDisplay: any, recordFieldAPIName: string) {
        // let isRecordFieldExist = false;
        return fieldsToDisplay.some((displayField: any, index: any) => {
            let isFieldExist = false;
            if (typeof displayField === 'object') {
                // const objKey = Object.keys(displayField).map(key => key)[0];
                // const referenceObject = fieldsToDisplay[objKey];

                isFieldExist = Object.keys(displayField).some((currentFieldKey: string) => {
                    return (currentFieldKey === recordFieldAPIName || displayField[currentFieldKey] === recordFieldAPIName);
                });
            } else {
                isFieldExist = (displayField === recordFieldAPIName);
            }
            return isFieldExist;
        });
    }

    /**
     * @param {string} templateName
     * @param {string} key
     * @param {string} value
     * @memberof TemplateConfigurationController
     */
    public updateFormData(templateName: string, key: string, value: string) {
        CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(templateName, key, value);
    }

    /**
     * @param {string} templateName
     * @param {string} key
     * @param {*} layoutMetaData
     * @memberof TemplateConfigurationController
     */
    public updateLayoutMetaData(templateName: string, key: string, layoutMetaData: any) {
        CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(templateName, key, layoutMetaData);
    }

    public async loginToDifferentOrg(oAuthRequest: any, environment: string) {
        await this.cqAPIService.login(oAuthRequest).then(async (connection : any) => {
            try {
                if (connection && connection.accessToken) {
                    await this.publishLWCToOtherOrg(connection.accessToken, connection.instanceUrl, environment);
                }
            }
            catch (error : any) {
                CQAppStore.loader.setVisibility(false);
                const errorObj = {
                    errorType: CQAppConstant.ERROR_CUSTOM,
                    errorMessage: error.error ? error.error : error.message,
                    errorStatus: error.status,
                    componentName: ''
                };
                CQAppStore.error.dispatchError(errorObj);
            }
        });
    }

    /**
   * @param {string} templateName
   * @memberof TemplateConfigurationController
   */
    public async publishLWCToOtherOrg(sfAccessToken: string, instanceUrl: string, environment: string) {
        try {
            const template: any = this.getSelectedTemplate();
            if (template.name.includes('cqGridGenerator')) {
                const outputConfigData = this.getTemplateWiseFormData(template.templateType);
                const ifNotValidJSON = outputConfigData.headerActions.some((action: any) => {
                    return (!action.hasOwnProperty('componentName') && !action.componentName);
                });
                if (ifNotValidJSON) {
                    alert('Please select Component Name for all the actions');
                } else {

                    const outputConfigData = this.getTemplateWiseFormData(template.templateType);
                    const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                    if (outputConfigData) {
                        const metadata = JSON.parse(JSON.stringify(outputConfigData));
                        if (template.name.includes('cqGridGenerator')) {
                            metadata.additionalFields = this.getAddtionalFields(template.templateType);
                            metadata.parentFields = [...CQAppConstant.DEFAULT_PARENT_FIELDS,...this.getParentFields(template.templateType)];
                            metadata.parentFields  = Array.from(new Set(metadata.parentFields));
                        }
                        else {
                            if (metadata.hasOwnProperty('additionalFields')) {
                                metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata)));
                            }
                            // Delete standard fields from metadata.fields object in Output config json
                            delete metadata.layout.metadata.fields['Name'];
                            delete metadata.layout.metadata.fields['RecordTypeId'];
                        }
                        // End Delete standard fields
                        CQAppStore.loader.setVisibility(true, '', true);
                        await this.cqAPIService.publishLWCToOtherOrg(versionNumber, template.name, metadata, instanceUrl, sfAccessToken, environment).then((res: any) => {
                            CQAppStore.loader.setVisibility(false);
                            if (res.success === false || res.status === 'Failed') {
                                let failureReasons = [];
                                if (res.details.componentFailures && Array.isArray(res.details.componentFailures)) {
                                    failureReasons = res.details.componentFailures.map((failure: any) => failure.problem);
                                } else {
                                    failureReasons.push(res.details.componentFailures.problem);
                                }
                                const errorObj = {
                                    errorType: CQAppConstant.DEPLOY_FAILED,
                                    errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                                    errorMessages: failureReasons,
                                    componentName: ''
                                };
                                CQAppStore.error.dispatchError(errorObj);
                            } else {
                                const successObj = {
                                    successMessage: 'Package is deployed successfully',
                                    componentName: '',
                                };
                                CQAppStore.success.dispatchError(successObj);
                            }
                            return res;
                        }).catch((error) => {
                            CQAppStore.loader.setVisibility(false);
                            const errorObj = {
                                errorType: CQAppConstant.UNKNOWN_PUBLISH_ERROR,
                                errorMessage: CQAppConstant.ERROR_MESSAGES.CORS_ERROR_MESSAGE,
                                errorStatus: error.status,
                                componentName: ''
                            };
                            CQAppStore.error.dispatchError(errorObj);
                        });
                    }
                }
            } else {
                const template: any = this.getSelectedTemplate();
                const outputConfigData = this.getTemplateWiseFormData(template.templateType);
                const versionNumber = this.cookie.get(CQAppConstant.VERSION_NUMBER);
                if (outputConfigData) {
                    const metadata = JSON.parse(JSON.stringify(outputConfigData));
                    if (template.name.includes('cqGridGenerator')) {
                        metadata.additionalFields = this.getAddtionalFields(template.templateType);
                    }
                    else {
                        if (metadata.hasOwnProperty('additionalFields')) {
                            metadata.additionalFields = Array.from(new Set(this.getAddtionalFieldsFromRules(metadata)));
                        }
                        // Delete standard fields from metadata.fields object in Output config json
                        delete metadata.layout.metadata.fields['Name'];
                        delete metadata.layout.metadata.fields['RecordTypeId'];
                    }
                    // End Delete standard fields
                    CQAppStore.loader.setVisibility(true, '', true);
                    await this.cqAPIService.publishLWCToOtherOrg(versionNumber, template.name, metadata, instanceUrl, sfAccessToken, environment).then((res: any) => {
                        CQAppStore.loader.setVisibility(false);
                        if (res.success === false || res.status === 'Failed') {
                            let failureReasons = [];
                            if (res.details.componentFailures && Array.isArray(res.details.componentFailures)) {
                                failureReasons = res.details.componentFailures.map((failure: any) => failure.problem);
                            } else {
                                failureReasons.push(res.details.componentFailures.problem);
                            }
                            const errorObj = {
                                errorType: CQAppConstant.DEPLOY_FAILED,
                                errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                                errorMessages: failureReasons,
                                componentName: ''
                            };
                            CQAppStore.error.dispatchError(errorObj);
                        } else {
                            const successObj = {
                                successMessage: 'Package is deployed successfully',
                                componentName: '',
                            };
                            CQAppStore.success.dispatchError(successObj);
                        }
                        return res;
                    }).catch((error) => {
                        CQAppStore.loader.setVisibility(false);
                        const errorObj = {
                            errorType: CQAppConstant.UNKNOWN_PUBLISH_ERROR,
                            errorMessage: CQAppConstant.ERROR_MESSAGES.CORS_ERROR_MESSAGE,
                            errorStatus: error.status,
                            componentName: ''
                        };
                        CQAppStore.error.dispatchError(errorObj);
                    });
                }
            }

        } catch (error : any) {
            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: CQAppConstant.ERROR_MESSAGES.PACKAGE_DEPLOYMENT_FAILURE,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    }

    /**
     * @param {string} templateName
     * @memberof TemplateConfigurationController
     */
    public getAddtionalFieldsFromRules(outputConfigData: any, templateName?: string) {
        let additionalFields: string[] = [];
        if (outputConfigData.hasOwnProperty('ruleElements')) {
            const fieldNames = Object.keys(outputConfigData.ruleElements);
            fieldNames.forEach((fieldAPIName: string) => {
                const rules = { ...outputConfigData['ruleElements'][fieldAPIName] };
                Object.keys(rules).forEach((ruleName: string) => {
                    const ruleWiseAdditionalFields = rules[ruleName];
                    if (ruleWiseAdditionalFields.hasOwnProperty('additionalFields')) {
                        additionalFields = [...additionalFields, ...ruleWiseAdditionalFields['additionalFields']];
                    }
                });
            });
        }

        if(templateName){
            additionalFields = this.removeFieldsFromAdditionalFields(templateName, additionalFields);
        }

        return additionalFields;
    }

    /**
     * removes lookup field with reference field from additional fields if present in UI layout fields if ends with name or caseNumber and falsy value
     * @param templateName name of the template name
     * @param additionalFields array of fields
     * @returns additionalFields
     */
    private removeFieldsFromAdditionalFields = (templateName: string, additionalFields: string[]): string[] => {
        let tempAdditionalFields = this.cqUtilityClass.createObjectDeepCopy(additionalFields);
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        if(layout&& layout.fields && layout.metadata){
            tempAdditionalFields.forEach((additionalField: string) => {
                const splitedField = additionalField.split('.');
                const fieldName = splitedField[0].replace('__r', '__c');
                if(splitedField.length > 1 && !additionalField.includes('RecordType') && layout.fields.includes(fieldName)){
                    const metadataField = layout.metadata.fields[fieldName];
                    if(metadataField.type === CQAppConstant.LOOKUP_TEXT || metadataField.type === CQAppConstant.MASTERDETAIL_TEXT){
                        let relationShipName = metadataField.relationshipName + '.';
                        relationShipName = relationShipName + (metadataField.referenceTo === 'Case' ? 'CaseNumber' : 'Name');
                        additionalFields = additionalFields.filter((additionalField: string) => additionalField !== relationShipName);
                    }
                }
            });
        }

        //filter out field that has falsy value
        additionalFields = additionalFields.filter((field: string) => {
            if(field){
                return true;
            }
        });

        return additionalFields;
    }

    public getSelectedObject(templateName: string) {
        return CQMasterDataStore.OutputConfig.getSelectedObject(templateName);
    }

    public getSelectedLayout(templateName: string) {
        return CQMasterDataStore.OutputConfig.getSelectedLayout(templateName);
    }

    public setSelectedObject(templateName: string, objectName: string) {
        CQMasterDataStore.OutputConfig.setSelectedObject(templateName, objectName);
    }

    // Below method can be used in case need to maintain rules that exist
    /**
     * @param {string} templateName
     * @memberof TemplateConfigurationController
     */
    public clearFieldRulesConfigurations(templateName: string) {
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        const outputConfigData = this.getTemplateWiseFormData(templateName);
        const removedLayoutFields: string[] = [];

        if (outputConfigData.hasOwnProperty('ruleElements')) {
            const fieldNames = Object.keys(outputConfigData.ruleElements);
            fieldNames.forEach((fieldAPIName: string) => {

                if (fieldAPIName !== 'componentLayout') {
                    const isFieldExist = layout.fields.some((layoutField: string) => layoutField === fieldAPIName);
                    if (!isFieldExist) {
                        removedLayoutFields.push(fieldAPIName);
                        const rules = { ...outputConfigData['ruleElements'][fieldAPIName] };
                        Object.keys(rules).forEach((ruleName: string) => {
                            if (ruleName === 'PicklistValueSource') {
                                this.deletePicklistValueSource(templateName, fieldAPIName);
                            } else {
                                this.deleteAppliedRule(fieldAPIName, ruleName, templateName);
                            }
                        });
                    }
                }
            });
        }

        // update Additional Fields in config output json if any layout field is removed from layout and referenced in filter based Rule
        this.updateAdditionalFieldsIfFieldRemoved(templateName);

    }

    /**
     * @param {string} fieldName
     * @param {string} ruleType
     * @param {string} selectedTemplateName
     * @memberof TemplateConfigurationController
     */
    public deleteAppliedRule(fieldName: string, ruleType: string, selectedTemplateName: string) {
        const componentEvents = Object.keys(CQAppConstant.COMPONENT_EVENT_RULES_MAP);
        const componentRules = componentEvents.map((eventName: string) => CQAppConstant.COMPONENT_EVENT_RULES_MAP[eventName].key);
        const inputRules = [...componentRules, ...CQAppConstant.RULE_TYPES_OBJECT.DEFAULT, 'transfer'];
        const isInputRules = inputRules.includes(ruleType);
        if (isInputRules) {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, ruleType, 'inputRules');
        } else if (ruleType === 'Formula Field - Hyperlink' || ruleType === 'Formula Field - Image') {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, ruleType, 'formulaFields');
        } else if (ruleType === 'dynamicSource') {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, ruleType, ruleType);
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, 'fields', 'lookupFilters');
        }
        else if (ruleType === 'lookupFilter') {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, '', 'lookupFilters');
        }
        else {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, ruleType, ruleType);
        }
        CQMasterDataStore.RulesObject.deleteAppliedRule(fieldName, ruleType);
        CQMasterDataStore.RuleElements.deleteAppliedRule(selectedTemplateName, fieldName, ruleType);
        CQMasterDataStore.Details.setAppliedRuleObject();
    }

    /**
    * @memberof TemplateConfigurationController
    */
    public deletePicklistValueSource = (selectedTemplateName: string, fieldName: string) => {
        const picklistObject = { ...CQMasterDataStore.PicklistValueSource.FieldPickListData };
        CQMasterDataStore.PicklistValueSource.deletePicklistValueSource(fieldName, picklistObject[fieldName]);
        const fieldRuleElement: any = { ...CQMasterDataStore.RuleElements.FieldRuleElement[fieldName] };
        if (fieldRuleElement && fieldRuleElement.hasOwnProperty('PicklistValueSource')) {
            CQMasterDataStore.RuleElements.deleteAppliedRule(selectedTemplateName, fieldName, 'PicklistValueSource');
        }
        const outputConfig = CQMasterDataStore.OutputConfig;
        if (outputConfig.getStoreKeyData(selectedTemplateName, 'picklistValueSource').hasOwnProperty(fieldName)) {
            CQMasterDataStore.OutputConfig.deleteRule(selectedTemplateName, fieldName, 'picklistValueSource', 'picklistValueSource');
        }

    }

    /**
    * @memberof TemplateConfigurationController
    */
    private updateAdditionalFieldsIfFieldRemoved = (templateName: string) => {
        // if (removedLayoutFields.length > 0) {
        let outputConfigData = this.getTemplateWiseFormData(templateName);

        outputConfigData = this.cqUtilityClass.createObjectDeepCopy(outputConfigData);
        const ruleElements = outputConfigData.ruleElements;

        const fieldNames = Object.keys(outputConfigData.ruleElements);

        fieldNames.forEach((fieldAPIName: string) => {
            const rules = { ...outputConfigData['ruleElements'][fieldAPIName] };

            Object.keys(rules).forEach((ruleName: string) => {

                if (ruleName === 'Formula Field - Hyperlink' || ruleName === 'Formula Field - Image') {
                    ruleElements[fieldAPIName][ruleName]['additionalFields'] = [...this.addAdditionalFieldsFormulaRule(templateName, rules[ruleName])];
                }
                if (CQAppConstant.TOGGLE_ACTION_RULE_TYPES.includes(ruleName)) {
                    ruleElements[fieldAPIName][ruleName]['additionalFields'] = [...this.addAdditionalToggleActionRule(templateName, rules[ruleName])];
                }
                if (ruleName === 'onLoad-setValues') {
                    ruleElements[fieldAPIName][ruleName]['additionalFields'] = [...this.addAdditionalOnLoadSetValueRule(templateName, rules[ruleName])];
                }
                if (ruleName === 'invoke') {
                    ruleElements[fieldAPIName][ruleName]['additionalFields'] = [...this.addAdditionalInvokeRule(templateName, rules[ruleName])];
                }
                if (ruleName === 'setValues') {
                    ruleElements[fieldAPIName][ruleName]['additionalFields'] = [...this.addAdditionalSetValueRule(templateName, rules[ruleName])];
                }

            });
        });

        CQMasterDataStore.RuleElements.setRuleElements(ruleElements);
        CQMasterDataStore.OutputConfig.setRuleElements(templateName, ruleElements);


    }

    /**
     * Remove field from additional field if present in layout
     */
    private removeAdditionalFieldPresentInLayout = (layout: any, additionalFields: string[]) => {
        const tempAdditionalFields = this.cqUtilityClass.createObjectDeepCopy(additionalFields)
        tempAdditionalFields.forEach((additionalField: string) => {
            additionalFields = additionalFields.filter((field) => !layout.fields.includes(field));
        });

        return additionalFields;
    }

    private addAdditionalFieldsFormulaRule(templateName: string, formulaRuleObject: any) {
        let additionalFields: string[] = [...formulaRuleObject.additionalFields];
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);

        const setObjects = [...formulaRuleObject.setObject];

        setObjects.length > 0 && setObjects.forEach((setObject: any) => {
            setObject.addedCriterias.forEach((criteria: any) => {
                const isFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.field.split('-')[0]);
                let isComparetoFieldExist = true;
                if (criteria.compareTo && criteria.compareTo !== true && criteria.compareTo !== false) {
                    isComparetoFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.compareTo.split('-')[0]);
                }
                if (!isFieldExist && !additionalFields.includes(criteria.field)) {
                    additionalFields.push(criteria.field);
                }
                if (criteria.isDynamic && !isComparetoFieldExist && !additionalFields.includes(criteria.compareTo)) {
                    additionalFields.push(criteria.compareTo);
                }
            });
        });
        additionalFields = this.removeAdditionalFieldPresentInLayout(layout, additionalFields);

        return additionalFields;
    }

    private addAdditionalToggleActionRule(templateName: string, toggleActionRuleObject: any) {
        let additionalFields: string[] = [...toggleActionRuleObject.additionalFields];
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        const addedCriterias = [...toggleActionRuleObject.addedCriterias];

        addedCriterias.forEach((criteria: any) => {
            const isFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.field.split('-')[0]);
            let isComparetoFieldExist = true;
            if (criteria.compareTo && criteria.compareTo !== true && criteria.compareTo !== false) {
                isComparetoFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.compareTo.split('-')[0]);
            }
            if (!isFieldExist && !additionalFields.includes(criteria.field)) {
                additionalFields.push(criteria.field);
            }
            if (criteria.isDynamic && !isComparetoFieldExist && !additionalFields.includes(criteria.compareTo)) {
                additionalFields.push(criteria.compareTo);
            }
        });
        additionalFields = this.removeAdditionalFieldPresentInLayout(layout, additionalFields);

        return additionalFields;
    }

    private addAdditionalOnLoadSetValueRule(templateName: string, onloadSetValuesRuleObj: any) {

        let additionalFields: string[] = [...onloadSetValuesRuleObj.additionalFields];
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        const setValues = [...onloadSetValuesRuleObj.setValues];

        setValues.length > 0 && setValues.forEach((setValue: any) => {
            setValue.addedCriterias.forEach((criteria: any) => {
                const isFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.field);
                let isComparetoFieldExist = true;
                if (criteria.compareTo && criteria.compareTo !== true && criteria.compareTo !== false) {
                    isComparetoFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.compareTo);
                }
                if (!isFieldExist && !additionalFields.includes(criteria.field) && !criteria.field.includes('-')) {
                    additionalFields.push(criteria.field);
                }
                if (criteria.isDynamic && !isComparetoFieldExist && !additionalFields.includes(criteria.compareTo) && !criteria.compareTo.includes('-')) {
                    additionalFields.push(criteria.compareTo);
                }
            });
        });
        additionalFields = this.removeAdditionalFieldPresentInLayout(layout, additionalFields);

        return additionalFields;
    }

    private addAdditionalInvokeRule(templateName: string, invokeRuleObject: any) {
        let additionalFields: string[] = [...invokeRuleObject.additionalFields];
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        const addedCriterias = [...invokeRuleObject.addedCriterias];

        addedCriterias.forEach((criteria: any) => {
            const isFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.field.split('-')[0]);
            let isComparetoFieldExist = true;
            if (criteria.compareTo && criteria.compareTo !== true && criteria.compareTo !== false) {
                isComparetoFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.compareTo.split('-')[0]);
            }
            if (!isFieldExist && !additionalFields.includes(criteria.field)) {
                additionalFields.push(criteria.field);
            }
            if (criteria.isDynamic && !isComparetoFieldExist && !additionalFields.includes(criteria.compareTo)) {
                additionalFields.push(criteria.compareTo);
            }
        });
        additionalFields = this.removeAdditionalFieldPresentInLayout(layout, additionalFields);

        return additionalFields;
    }

    private addAdditionalSetValueRule(templateName: string, setValueRuleObject: any) {
        let additionalFields: string[] = [...setValueRuleObject.additionalFields];
        const layout = CQMasterDataStore.OutputConfig.getTemplateLayoutMetadata(templateName);
        const setValues = [...setValueRuleObject.setValues];
        setValues.length > 0 && setValues.forEach((setValue: any) => {
            setValue.addedCriterias.forEach((criteria: any) => {
                const isFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.field.split('-')[0]);
                let isComparetoFieldExist = true;
                if (criteria.compareTo && criteria.compareTo !== true && criteria.compareTo !== false) {
                    isComparetoFieldExist = layout.fields.some((layoutField: string) => layoutField === criteria.compareTo.split('-')[0]);
                }
                if (!isFieldExist && !additionalFields.includes(criteria.field)) {
                    additionalFields.push(criteria.field);
                }
                if (criteria.isDynamic && !isComparetoFieldExist && !additionalFields.includes(criteria.compareTo)) {
                    additionalFields.push(criteria.compareTo);
                }
            });
        });
        additionalFields = this.removeAdditionalFieldPresentInLayout(layout, additionalFields);

        return additionalFields;
    }

}
